import {
  getReferenceString,
  type MedplumClient,
  type PatchOperation,
  type QueryTypes,
  resolveId,
} from "@medplum/core";
import type { Communication } from "@medplum/fhirtypes";
import type { Practice } from "../practice/Practice";
import { type ExtensionPatchRequest, findExtension, getExtensionPatchOperations } from "../utils";

export const VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES = "vibrant.emailTemplates";
const VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES_SUBJECT = "vibrant.emailTemplates.subject";
const VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES_BODY_MARKDOWN = "vibrant.emailTemplates.bodyMarkdown";
const VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES_BODY_RAW = "vibrant.emailTemplates.bodyRaw";

// Communication has no "retired" code. A template is stored as `preparation` (an email not yet
// sent), so `stopped` reads as "this one will not be offered again". Product calls it archived.
export const ARCHIVED_STATUS = "stopped";

export interface CreateEmailTemplateRequest {
  practiceId: string;
  name: string;
  subject: string;
  bodyMarkdown: string;
  bodyRaw: string;
}

export interface EmailTemplateSearchQuery {
  practice?: Practice;
  name?: string;
}

export interface EmailTemplatePatchQuery {
  name?: string;
  subject?: string;
  bodyMarkdown?: string;
  bodyRaw?: string;
}

export type SerializedEmailTemplate = Communication;

export class EmailTemplate {
  constructor(readonly communication: Communication) {}

  get id(): string {
    return this.communication.id!;
  }

  get name(): string {
    return this.communication.topic?.text || "(no name)";
  }

  get subject(): string {
    return (
      findExtension(this.communication, VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES_SUBJECT)
        ?.valueString || "(no subject)"
    );
  }

  get bodyMarkdown(): string {
    return (
      findExtension(this.communication, VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES_BODY_MARKDOWN)
        ?.valueString || "(no body)"
    );
  }

  get bodyRaw(): string {
    return (
      findExtension(this.communication, VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES_BODY_RAW)
        ?.valueString || "(no body)"
    );
  }

  get isArchived(): boolean {
    return this.communication.status === ARCHIVED_STATUS;
  }

  get practiceId(): string {
    const practiceId = resolveId(this.communication.sender);
    if (!practiceId) {
      throw new Error("Email template has no sender");
    }
    return practiceId;
  }

  serialize(): SerializedEmailTemplate {
    return this.communication;
  }
}

export async function createEmailTemplate(
  medplum: MedplumClient,
  request: CreateEmailTemplateRequest,
): Promise<EmailTemplate> {
  const communication = await medplum.createResource<Communication>({
    resourceType: "Communication",
    status: "preparation",
    sender: {
      reference: `Organization/${request.practiceId}`,
    },
    category: [{ coding: [{ code: "emailtemplate", display: "Email Template" }] }],
    topic: {
      text: request.name,
    },
    extension: [
      {
        url: VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES_SUBJECT,
        valueString: request.subject,
      },
      {
        url: VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES_BODY_MARKDOWN,
        valueString: request.bodyMarkdown,
      },
      {
        url: VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES_BODY_RAW,
        valueString: request.bodyRaw,
      },
    ],
  });
  return new EmailTemplate(communication);
}

export async function getEmailTemplate(medplum: MedplumClient, id: string): Promise<EmailTemplate> {
  const communication = await medplum.readResource("Communication", id);
  return new EmailTemplate(communication);
}

export async function searchEmailTemplates(
  medplum: MedplumClient,
  searchQuery: EmailTemplateSearchQuery,
): Promise<EmailTemplate[]> {
  // if searchquery has no keys, return null
  if (Object.keys(searchQuery).length === 0) {
    return [];
  }

  const queryTypes: QueryTypes = {
    category: "emailtemplate",
    "status:not": ARCHIVED_STATUS,
  };

  if (searchQuery.practice) {
    queryTypes.sender = getReferenceString(searchQuery.practice.serialize());
  }

  if (searchQuery.name) {
    queryTypes.topic = searchQuery.name;
  }

  const communications = await medplum.searchResources("Communication", queryTypes);
  return communications.map((communication) => new EmailTemplate(communication));
}

export async function patchEmailTemplate(
  medplum: MedplumClient,
  emailTemplateId: string,
  patchQuery: EmailTemplatePatchQuery,
): Promise<EmailTemplate> {
  const communication = await medplum.readResource("Communication", emailTemplateId);
  const patchOperations: PatchOperation[] = [];

  if (patchQuery.name) {
    if (communication.topic?.text) {
      patchOperations.push({
        op: "replace",
        path: "/topic/text",
        value: patchQuery.name,
      });
    } else {
      patchOperations.push({
        op: "add",
        path: "/topic/text",
        value: patchQuery.name,
      });
    }
  }

  const extensionPatchRequests: ExtensionPatchRequest[] = [];

  if (patchQuery.subject) {
    extensionPatchRequests.push({
      resource: communication,
      url: VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES_SUBJECT,
      value: patchQuery.subject,
    });
  }

  if (patchQuery.bodyMarkdown) {
    extensionPatchRequests.push({
      resource: communication,
      url: VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES_BODY_MARKDOWN,
      value: patchQuery.bodyMarkdown,
    });
  }

  if (patchQuery.bodyRaw) {
    extensionPatchRequests.push({
      resource: communication,
      url: VIBRANT_EXTENSION_URL_EMAIL_TEMPLATES_BODY_RAW,
      value: patchQuery.bodyRaw,
    });
  }

  const patchedCommunication = await medplum.patchResource("Communication", emailTemplateId, [
    ...patchOperations,
    ...getExtensionPatchOperations(communication, extensionPatchRequests),
  ]);

  return new EmailTemplate(patchedCommunication);
}

/**
 * Archives a template: it drops out of the list and the service picker.
 *
 * A status change, not a delete. A service that already sends this template keeps sending it,
 * because the send path reads the template by id. So archiving never silently stops a patient email.
 */
export async function archiveEmailTemplate(
  medplum: MedplumClient,
  emailTemplateId: string,
): Promise<EmailTemplate> {
  const communication = await medplum.patchResource("Communication", emailTemplateId, [
    { op: "replace", path: "/status", value: ARCHIVED_STATUS },
  ]);
  return new EmailTemplate(communication);
}
